import { tz } from "@date-fns/tz";
import classNames from "classnames";
import { differenceInDays, parse, startOfDay, startOfToday } from "date-fns";
import React from "react";
import { Icon, Label } from "semantic-ui-react";
import styled from "styled-components";
import { useTimezone } from "../hooks/useTimezone";
import { ZonedDateTime } from "./global_components/ZonedDateTime";

export const StyledLabel = styled(Label)<{ color: string; backgroundColor: string }>`
  &&&& {
    color: ${(props: any) => props.color} !important;
    background-color: ${(props: any) => props.backgroundColor} !important;
  }
`;

export const IdBadge = ({ url, text, basic }: { url: string; text: string; basic?: boolean }) => {
  return (
    <Label
      basic={basic}
      size='mini'
      onClick={(e: any) => {
        e.stopPropagation();
      }}
    >
      <a
        href={url}
        target='_blank'
        rel='noopener noreferrer'
        style={{ color: "black", fontSize: "12px", paddingLeft: "6px" }}
      >
        {text}
        <Icon name='external square alternate' size='large' />
      </a>
    </Label>
  );
};

export const DateTimeBadge = ({ value, oneline }: any) => (
  <Label size='small'>
    <b>
      {oneline ? (
        <ZonedDateTime value={value} formatString='MMM dd, yyyy h:mm a' />
      ) : (
        <>
          <ZonedDateTime value={value} formatString='MMM dd, yyyy' disableAutoTimezoneIndicator />
          <br />
          <br />
          <ZonedDateTime value={value} formatString='h:mm a' />
        </>
      )}
    </b>
  </Label>
);

export const DateBadge = ({ value }: any) => {
  if ([null, "", "0000-00-00"].includes(value)) {
    return (
      <Label color='grey' size='small'>
        No Due Date
      </Label>
    );
  }
  return (
    <Label size='small'>
      <b>
        <ZonedDateTime value={value} timezone='UTC' formatString='MMM dd, yyyy' disableAutoTimezoneIndicator />
      </b>
    </Label>
  );
};

export function calculateDaysUntilDue(value: string, timezone: string) {
  const today = startOfToday({ in: tz(timezone) });
  const dueDate = startOfDay(parse(value.split("T")[0], "yyyy-MM-dd", new Date(), { in: tz(timezone) }));
  const diff = differenceInDays(dueDate, today);

  return {
    diff,
    dueDate,
  };
}

export const DateDueBadge = ({ value, oneLine }: { value: string | null; oneLine?: boolean }) => {
  const {
    defaultTimezoneInfo: { timezone },
  } = useTimezone();

  if (value === null || value === "" || value === "0000-00-00") {
    return (
      <Label color='grey' size='small'>
        No Due Date
      </Label>
    );
  }

  const { diff, dueDate } = calculateDaysUntilDue(value, timezone);
  const dueDateIsNotToday = diff !== 0;

  const buildColor = (diff: number) => {
    if (diff <= -1) return { backgroundColor: "#ffe8e8", color: "#da2828" };
    if (diff === 0) return { backgroundColor: "#e9f6ff", color: "#2284d0" };
    if (diff >= 1) return { backgroundColor: "#EAFDEF", color: "#228337" };
    throw new Error("Invalid diff");
  };

  const buildText = (diff: number) => {
    const days = Math.abs(diff);
    if (diff <= -2) return `${days} Days Ago`;
    if (diff === -1) return `${days} Day Ago`;
    if (diff === 0) return "Today";
    if (diff === 1) return `${days} Day`;
    if (diff >= 2) return `${days} Days`;
    throw new Error("Invalid diff");
  };

  const { backgroundColor, color } = buildColor(diff);
  const text = buildText(diff);

  return (
    <StyledLabel basic color={color} backgroundColor={backgroundColor} size='small'>
      <b
        className={classNames({
          "tw-flex tw-gap-1": true,
          "tw-items-center": oneLine,
          "tw-flex-col": !oneLine && dueDateIsNotToday,
        })}
      >
        {dueDateIsNotToday && (
          <ZonedDateTime value={dueDate} formatString='MMM dd, yyyy' disableAutoTimezoneIndicator />
        )}
        <span>{text}</span>
      </b>
    </StyledLabel>
  );
};
